import { OPPORTUNITY_LOAD_BUFFER_IN_HOURS } from '@bluebrick/discounts-service-module'
import { DateTime } from 'luxon'
import { getAnonymizedEventName, getDifferenceInDays, getSortedByDate, isOpportunityEvent } from '@/common/utils'
import type { DiscountConfig, EventData, HistoryEvent, HistoryTableItem, StoreData } from '@/types'

/**
 * Returns true if given start date relates to a first upcoming event.
 * If there is an earlier timeline event for given product which has a recommendation
 * then function returns false.
 * This originates from an assumption that depth recommendations make sense only for the nearest
 * upcoming event for given product
 */
export function isDepthRecommendationAvailable(
	storeData: StoreData,
	product: string,
	eventStartDate: DateTime,
	editedEventId: string | null
): boolean {
	const productEvents = storeData.skus.find(({ human_name }) => human_name === product)?.events

	const nearestEventWithRecommendation = productEvents?.find(({ isRecommendationAvailable }) => isRecommendationAvailable)

	if (!nearestEventWithRecommendation || (editedEventId !== null && nearestEventWithRecommendation.id === editedEventId)) {
		return true
	}

	return eventStartDate < nearestEventWithRecommendation.start_date
}

export function findNextSlot(storeData: StoreData, uniqueSkuId: string, date: DateTime): EventData | undefined {
	const sku = storeData.skus.find((sku) => sku.unique_sku_id === uniqueSkuId)

	const events = sku?.events

	if (!events) {
		return undefined
	}

	const event = getSortedByDate([...events]).find((event) => isOpportunityEvent(event) && event.start_date > date)

	if (!event?.start_date?.isValid || !event.end_date?.isValid) {
		return
	}

	return event
}

export function mapHistoryEventToDomain(item: HistoryEvent, shouldAnonymizeData?: boolean): HistoryTableItem {
	return {
		id: `${item.unique_sku_id}-${item.datetime_from}`,
		eventName: shouldAnonymizeData ? getAnonymizedEventName(item.event_name) || item.event_name : item.event_name,
		startDate: item.datetime_from,
		endDate: item.datetime_to,
		units: item.units,
		major: item.major,
		baseline: item.baseline_revenue,
		revenue: item.revenue,
		uplift: item.uplift_revenue,
		upliftScore: item.uplift_score,
		promoLength: Math.floor(getDifferenceInDays(item.datetime_from, item.datetime_to)),
		discountDepth: item.discount_depth
	}
}

/**
 * Returns nearest start date of an event.
 * We show the first opportunity at the default event start hour on current day, unless it is less than 4 hours away.
 * In such case we change it to the default event start hour on next day.
 */
export function getDefaultNearestStartDate(discountsConfig: DiscountConfig) {
	const now = DateTime.now().toUTC()

	let result = now.setZone(discountsConfig.ianaTimeZoneName) as DateTime<true>

	/**
	 * For Nintendo there is a leadtime - this means you need to submit a discount at least X days before it happens.
	 * Take that into account when showing opportunities for Nintendo.
	 **/
	if (discountsConfig.leadTimeInDays) {
		result = result.plus({ days: discountsConfig.leadTimeInDays })
	}

	result = result.set({
		hour: discountsConfig.startTimeRules.hour.default,
		minute: discountsConfig.startTimeRules.minute.default,
		second: discountsConfig.startTimeRules.second.default
	})

	const hourDiff = Math.abs(result.toUTC().diff(now, 'hours').hours)

	if (hourDiff < OPPORTUNITY_LOAD_BUFFER_IN_HOURS || now > result.toUTC()) {
		result = result.plus({ days: 1 })
	}

	return result.toUTC()
}
